import {
  CSSProperties,
  FC,
  JSX,
  memo,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Portal } from 'components/Shared/Portal';
import { useHooryWidgetHiderCount } from 'hooks/third-party/useHooryWidget';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { usePageNoScroll } from 'hooks/usePageNoScroll';
import * as Styled from './styles';
import { Close } from 'icons/Close';
import { PORTAL_ID_MODAL_BOTTOM } from 'constants/app/portal-id-modal-bottom';
import { EGlobalClassnames } from 'constants/dom/global-classnames';
import { TSizeType } from 'interfaces';

interface ModalProps {
  children: JSX.Element[] | JSX.Element;
  isOpen: boolean;
  onClose: () => void;
  onBeforeClose?: () => void;
  closable?: boolean;
  fullwidth?: boolean;
  closeIconSize?: Omit<TSizeType, 'huge' | 'medium'>;
  hideCloseIcon?: boolean;
  style?: CSSProperties;
  bodyTransparent?: boolean;
}
interface ClosableModalProps extends Omit<ModalProps, 'isOpen'> {
  dialogRef: RefObject<HTMLDivElement>;
  hideCloseIcon?: boolean;
}

export const Modal: FC<ModalProps> = memo(
  ({
    children,
    isOpen,
    onClose,
    closable = true,
    onBeforeClose,
    fullwidth,
    closeIconSize = 'large',
    hideCloseIcon = false,
    style,
    bodyTransparent = false
  }) => {
    usePageNoScroll(isOpen);
    useHooryWidgetHiderCount({ hiderType: 'modal', open: isOpen });
    const [animation, setAnimation] = useState('open');
    const [display, setDisplay] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const ref = useRef<HTMLDivElement>(null);

    const hide = async () => {
      setAnimation('close');
      clearTimeout(timeoutRef.current);
      await new Promise(r => (timeoutRef.current = setTimeout(r, 300)));
      setDisplay(false);
      onClose();
    };

    const handleEscapeKeyDown = useCallback(
      (e: KeyboardEvent) => {
        const antImagePreviewControls = document.querySelector(
          '.ant-image-preview-operations-wrapper'
        );
        // check if antd image preview is open and not closing on escape
        if (antImagePreviewControls) {
          return;
        }
        if (closable && ref.current && e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      },
      [onClose]
    );

    useEffect(() => {
      // TODO: to consider refactor to use keyup event after resigning antd
      window.addEventListener('keydown', handleEscapeKeyDown, true);
      return () => {
        window.removeEventListener('keydown', handleEscapeKeyDown, true);
      };
    }, []);

    useEffect(() => {
      if (!isOpen) {
        hide();
      } else {
        clearTimeout(timeoutRef.current);
        setDisplay(true);
        setAnimation('open');
      }
    }, [isOpen]);

    return display ? (
      <Portal>
        <Styled.Modal
          className={`modal ${EGlobalClassnames.Modal} ${animation}`}
          $fullwidth={fullwidth}
          style={style}
        >
          <Styled.ModalOverlay />
          <Styled.ModalDialog ref={ref} className="modal-dialog">
            {closable ? (
              <ClosableModal
                dialogRef={ref}
                onClose={onBeforeClose || hide}
                closeIconSize={closeIconSize}
                hideCloseIcon={hideCloseIcon}
              >
                {children}
              </ClosableModal>
            ) : (
              <Styled.ModalBody
                $bodyTransparent={bodyTransparent}
                className="modal-body"
              >
                {children}
              </Styled.ModalBody>
            )}
            <div id={PORTAL_ID_MODAL_BOTTOM}></div>
          </Styled.ModalDialog>
        </Styled.Modal>
      </Portal>
    ) : null;
  }
);

const ClosableModal: FC<ClosableModalProps> = ({
  onClose,
  children,
  dialogRef,
  closeIconSize = 'large',
  hideCloseIcon = false
}) => {
  useOutsideClick(dialogRef, onClose);
  return (
    <Styled.ModalBody className="modal-body">
      {!hideCloseIcon && (
        <Styled.CloseButton onClick={onClose} size={closeIconSize}>
          <Close />
        </Styled.CloseButton>
      )}
      {children}
    </Styled.ModalBody>
  );
};
